import React, { useContext, useEffect, useReducer, useRef, useState } from "react";
import _ from "lodash";
import * as notify from "../../../shared/notify";
import eventEmitter from "../../../shared/events";

import NewSupplier from "../supplier/new";
import NotificationBar from "../../notification-bar";
import SLACounts from "../sla-counts";

import { DATE_FNS_SHORT_DATE_FORMAT } from "../../../shared/constants";
import { Input, InputList, Select, Checkbox, DatePicker, NumberField as Number } from "../../form-inputs/inputs";
import { Typeahead } from "../../form-inputs/typeahead";
import { VisibleDisabledFeature } from "../../../shared/future-feature-helpers";
import { reducer, initialState } from "./reducer";
import { ViewOnlyPermissionsContext } from "contexts/view-only-permissions/view-only-permissions";
import { ShipmentContext } from "../contexts";
import { SHIPMENT_TYPE_MAP } from "../../shipments/lib/constants";

import {
    updateHasDefaultPremiumEditing,
    updateHasDefaultClientImageApproval,
    updateIsFoodservice,
    updateSendToGdsn,
} from "../lib/shipmentProductOptionStore";

import {
    getCarriers,
    getLookups,
    getClients,
    getPaymentMethods,
    getReceivers,
    saveShipment,
    getSuppliers,
    getLocations,
    getItemMasterShipmentTypes,
    getSLACounts,
    getProductLimits,
} from "../lib/shipmentData";
import { dialogConfirm } from "../../../shared/dialog";
import { OverrideDates } from "../components/override-dates";
import { useCalculateDueDate } from "../hooks/use-calculate-due-date";
import { format } from "date-fns";
import { BRAND_VERIFIED_LOCATION } from "../lib/constants";
import { isInteger } from "../../task-list/lib/utils";
import { useLocation } from "react-router-dom";
import { physicalPeoriaAndVMPTurnaroundOptions, turnaroundOptions } from "../../../admin/clients/lib/consts";
import { useSettings } from "../../../shared/hooks/use-settings";
import { ErrorLabel } from "../../form-inputs/shared/error-label";

const validateFinalizedLock = (name) => {
    switch (name) {
        case "carrier":
        case "carrierTrackingNumber":
        case "finalDestination":
        case "isOnHold":
        case "isRush":
        case "locationValue":
        case "supplier":
            notify.error(`Could not change ${name} because the current shipment contains UPCs that have already been finalized.`);

            return false;
        default:
            return true;
    }
};

var updateDatesTimer = null;
const FULL_STEP = 1;
const HALF_STEP = 0.5;
const locationPeoria = "Peoria";
const locationCxhDistribution = "Distribution (CxH)";

const ShipmentInformation = (props) => {
    const { settings } = useSettings();
    const location = useLocation();
    const isShipmentEditor = location.pathname.includes("/shipment-editor");
    const saveButtonRef = useRef();
    const [isAddSupplier, setIsAddSupplier] = useState(false);
    const { viewOnlyShipment } = useContext(ViewOnlyPermissionsContext);
    const shipment = useContext(ShipmentContext);
    const shipmentContext = props.shipmentContext ? props.shipmentContext : shipment;
    const { shipmentData, updateShipmentData } = shipmentContext;
    const reducerInitialState = shipmentData ? { ...initialState, ...shipmentData } : initialState;
    const [state, dispatch] = useReducer(reducer, reducerInitialState);
    const { mutate: calculateDueDate } = useCalculateDueDate();
    const [lookups, setLookups] = useState({
        carriers: [],
        clientsCxH: [],
        finalDestination: [],
        itemMasterShipmentTypes: [],
        locations: [],
        paymentMethods: [],
        receivers: [],
        suppliers: [],
        isLoaded: false,
    });
    const isDigital = SHIPMENT_TYPE_MAP[shipmentData.shipmentType].isDigital;
    const isBrandVerified = shipmentData?.locationValue === BRAND_VERIFIED_LOCATION;
    const turnAroundTimeStep = isBrandVerified ? HALF_STEP : FULL_STEP;
    const isPeoria = state.locationValue === locationPeoria;
    const isVMP = state.locationValue === locationCxhDistribution && isDigital;

    useEffect(() => {
        async function fetchData() {
            const carriers = await getCarriers();
            const lookupLists = await getLookups();
            const clientsCxH = await getClients({ clientFilter: 3 });
            const paymentMethods = await getPaymentMethods();
            const receivers = await getReceivers(shipmentData);
            const suppliers = await getSuppliers();
            const locations = await getLocations();
            const itemMasterShipmentTypes = await getItemMasterShipmentTypes();

            setLookups({
                ...lookupLists,
                carriers,
                clientsCxH,
                itemMasterShipmentTypes,
                locations,
                paymentMethods,
                receivers,
                suppliers,
                isLoaded: true,
            });
        }

        fetchData();

        eventEmitter.on("SLA_SUGGESTED_DATE", handleSuggestedDate);

        return () => {
            eventEmitter.removeListener("SLA_SUGGESTED_DATE", handleSuggestedDate);
        };
    }, []);

    useEffect(() => {
        calculateDueDate(
            {
                isRush: state.isRush,
                shipmentReceiveDate: format(new Date(state.startDate), DATE_FNS_SHORT_DATE_FORMAT),
                turnAroundDays: state.turnAroundDays,
                cxHCompany: state.cxHCompany,
                clientsCxH: lookups.clientsCxH,
            },
            {
                onSuccess: (additionalChanges) => {
                    dispatch({ type: "UPDATE_DATES", additionalChanges });
                    eventEmitter.on("UPDATE_SLA", reloadSLACounts);
                },
            }
        );

        eventEmitter.on("UPDATE_SLA", reloadSLACounts);

        return () => {
            eventEmitter.removeListener("UPDATE_SLA", reloadSLACounts);
        };
    }, [lookups]); // it has no deps to state, may contain values from previous renders - refactor?

    useEffect(() => {
        if (state.cxHCompany && !shipmentData.isCompleted) {
            const client = lookups.clientsCxH.find(({ value }) => value === state.cxHCompany);

            if (client) {
                updateField("sendToGdsn", client.sendToGdsn);
            }
        }
    }, [state.cxHCompany, lookups.clientsCxH]);

    useEffect(() => {
        updateHasDefaultPremiumEditing(state.hasPremiumEditingShipmentDefault);
    }, [state.hasPremiumEditingShipmentDefault]);

    useEffect(() => {
        updateHasDefaultClientImageApproval(state.imagesNeedClientApprovalDefault);
    }, [state.imagesNeedClientApprovalDefault]);

    useEffect(() => {
        updateIsFoodservice(state.isFoodService);
    }, [state.isFoodService]);

    useEffect(() => {
        updateSendToGdsn(state.sendToGdsn);
    }, [state.sendToGdsn]);

    useEffect(() => {
        if (state.completeOnSave) {
            save(() => {
                eventEmitter.emit("COMPLETE_SHIPMENT");
            });
        }
    }, [state.completeOnSave]);

    // Sync the Start Date when moved to next week
    useEffect(() => {
        if (!shipmentData.startDate) {
            return;
        }

        const contextStartDate = format(new Date(shipmentData.startDate), DATE_FNS_SHORT_DATE_FORMAT);
        const currentStartDate = state.startDate ? format(new Date(state.startDate), DATE_FNS_SHORT_DATE_FORMAT) : null;

        if (contextStartDate !== currentStartDate) {
            dispatch({ type: "UPDATE_DATES", additionalChanges: { startDate: shipmentData.startDate } });
        }
    }, [shipmentData.startDate]);

    useEffect(() => {
        if (updateDatesTimer) {
            clearTimeout(updateDatesTimer);
        }

        // Debounce this update
        const updateDate = async () => {
            if (!state.startDate) {
                return;
            }

            calculateDueDate(
                {
                    isRush: state.isRush,
                    shipmentReceiveDate: format(new Date(state.startDate), DATE_FNS_SHORT_DATE_FORMAT),
                    turnAroundDays: state.turnAroundDays,
                    cxHCompany: state.cxHCompany,
                    clientsCxH: lookups.clientsCxH,
                },
                {
                    onSuccess: (additionalChanges) => {
                        dispatch({ type: "UPDATE_DATES", additionalChanges });
                    },
                }
            );
        };

        updateDatesTimer = setTimeout(() => {
            updateDate();
        }, 600);
    }, [state.turnAroundDays, state.startDate]);

    const reloadSLACounts = async () => {
        if (state.cxHCompany) {
            const cxhClient = state.cxHCompany ? lookups.clientsCxH.find((c) => c.value === state.cxHCompany) : null;
            const sla_counts = cxhClient ? await getSLACounts(cxhClient.id, format(new Date(state.startDate), DATE_FNS_SHORT_DATE_FORMAT)) : {};

            dispatch({ type: "UPDATE_DATES", additionalChanges: { sla_counts } });
        }
    };

    const handleSuggestedDate = (additionalChanges) => {
        dispatch({ type: "UPDATE_DATES", additionalChanges });
    };

    const updateField = async (name, value) => {
        if (state.key && shipmentData.hasFinalizedUpc && !validateFinalizedLock(name)) {
            return;
        }

        eventEmitter.emit("SHIPMENT_INFO_SAVED", false);

        const additionalChanges = await getAdditionalChanges(name, value);
        dispatch({ type: "UPDATE_STATE", key: name, value, additionalChanges });
    };
    const updateTypeahead = async (name, value) => {
        if (state.key && shipmentData.hasFinalizedUpc && !validateFinalizedLock(name)) {
            return;
        }

        eventEmitter.emit("SHIPMENT_INFO_SAVED", false);

        const additionalChanges = await getAdditionalChanges(name, value);
        dispatch({ type: "UPDATE_STATE", key: name, value: value.value, additionalChanges });
    };

    const getClientTurnAroundDays = (clientValue) => {
        const client = lookups.clientsCxH.find((c) => c.value === clientValue);

        return client ? client.turnAroundDays : settings.slaV2Options.defaultTurnAroundTime;
    };

    const getAdditionalChanges = async (name, value) => {
        const additionalChanges = {};
        switch (name) {
            case "locationValue":
                if (
                    ((!isDigital && value === locationPeoria) || (isDigital && value === locationCxhDistribution)) &&
                    state.turnAroundDays === turnaroundOptions[1].text
                ) {
                    additionalChanges.turnAroundDays = undefined;
                }

                break;
            case "cxHCompany":
                const cxhClient = lookups.clientsCxH.find((c) => c.value === value.value);

                /* Client Requires Premium */
                const isEitherClientPremium = cxhClient && cxhClient.hasPremiumEditing;
                let hasPremiumEditingShipmentDefault = state.hasPremiumEditingShipmentDefault;

                if (!state.hasPremiumEditingShipmentDefault && cxhClient && cxhClient.hasPremiumEditing) {
                    hasPremiumEditingShipmentDefault = true;
                    notify.inform("Premium client has enabled Premium Editing");
                }

                if (hasPremiumEditingShipmentDefault && !isEitherClientPremium) {
                    hasPremiumEditingShipmentDefault = await notify.confirm("Premium Client removed, would you like to keep premium editing?");
                }

                additionalChanges.hasPremiumEditingShipmentDefault = hasPremiumEditingShipmentDefault;

                /* Client Requires Image Approval */
                const isEitherClientImageApproval = cxhClient && cxhClient.imagesNeedClientApprovalDefault;
                let imagesNeedClientApprovalDefault = state.imagesNeedClientApprovalDefault;

                if (!state.imagesNeedClientApprovalDefault && cxhClient && cxhClient.imagesNeedClientApprovalDefault) {
                    imagesNeedClientApprovalDefault = true;
                    notify.inform("Client image approval enabled");
                }

                if (imagesNeedClientApprovalDefault && !isEitherClientImageApproval) {
                    imagesNeedClientApprovalDefault = await notify.confirm(
                        "Client requiring image approval removed, would you like to keep client approval?"
                    );
                }

                additionalChanges.imagesNeedClientApprovalDefault = imagesNeedClientApprovalDefault;

                // SLA
                const sla_counts = cxhClient ? await getSLACounts(cxhClient.id, format(new Date(state.startDate), DATE_FNS_SHORT_DATE_FORMAT)) : {};
                additionalChanges.sla_counts = sla_counts;
                additionalChanges.turnAroundDays = cxhClient ? getClientTurnAroundDays(cxhClient.value) : undefined;

                break;
            case "holdReleaseDate":
                if (value) {
                    additionalChanges.isOnHold = true;
                }

                break;
            case "isCmp":
                if (!value) {
                    additionalChanges.paymentMethod = "";
                    additionalChanges.paymentReferenceNumber = "";
                    additionalChanges.isCmpConfirmed = false;
                }

                break;
            case "isOnhold":
                if (!value) {
                    additionalChanges.holdReleaseDate = null;
                }

                break;
            case "isRush":
                if (!value) {
                    additionalChanges.turnAroundDays = getClientTurnAroundDays(state.cxHCompany);
                    additionalChanges.rushDays = "";
                    additionalChanges.rushConfirmedBy = "";
                }

                break;
            default:
                break;
        }

        return additionalChanges;
    };

    const updateSuppliers = async (newSupplierCode) => {
        const suppliers = await getSuppliers();
        const newLookups = { ...lookups, suppliers };
        setLookups(newLookups);
        updateField("supplier", newSupplierCode);
        setIsAddSupplier(false);
    };

    const save = async (completeCallback) => {
        if (state.turnAroundDays && !isBrandVerified && !isInteger(state.turnAroundDays)) {
            notify.error("Turn Around Time must be an integer");

            return;
        }

        if (!state.turnAroundDays) {
            notify.error("Turn Around Time is required");

            return;
        }

        if (!state.cxHCompany) {
            notify.error("CES Client is required");

            return;
        }

        if (!state.receivedBy) {
            notify.error("Receiver is required");

            return;
        }

        if (!state.supplier && !state.isMissingPaperwork) {
            notify.error("Supplier is required");

            return;
        }

        const formattedStartDate = format(new Date(state.startDate), DATE_FNS_SHORT_DATE_FORMAT);
        const startDateChanged =
            !!shipmentData.startDate && formattedStartDate !== format(new Date(shipmentData.startDate), DATE_FNS_SHORT_DATE_FORMAT);

        let overrideClientProductLimits;

        if (state.key && startDateChanged) {
            const productLimits = await getProductLimits(state.key, { date: formattedStartDate });

            if (productLimits.isClientLimitExceeded) {
                const confirmChange = await dialogConfirm({
                    data: {
                        suggested: productLimits.availableShipmentStartDate,
                    },
                    Component: OverrideDates,
                });

                if (confirmChange.cancel) {
                    return;
                }

                // Override -> save with the selected date; Move -> BE moves it to the next available week.
                overrideClientProductLimits = confirmChange.update;
            }
        }

        saveButtonRef.current.setAttribute("disabled", "disabled");

        const errorElements = Array.from(document.querySelectorAll(".-error"));
        for (let errorElement of errorElements) {
            errorElement.classList.remove("-error");
        }

        const payload = { ...state, startDate: formattedStartDate };

        if (overrideClientProductLimits !== undefined) {
            payload.overrideClientProductLimits = overrideClientProductLimits;
        }

        const response = await saveShipment(payload);

        if (saveButtonRef && saveButtonRef.current) {
            saveButtonRef.current.removeAttribute("disabled");
        }

        if (!response) {
        } else if (notify.errorResponse(response)) {
            const errorFields = response.errors.map((err) => {
                return err.field;
            });
            errorFields.forEach((field, i) => {
                if (!field) {
                    return;
                }

                const $field = document.getElementById(field.charAt(0).toLowerCase() + field.slice(1));

                if ($field) {
                    $field.classList.add("-error");

                    if (i === 0) {
                        $field.focus();
                    }
                }
            });
        } else {
            response.hasMetricMeasurementsOnly = response.cxHClient.hasMetricMeasurementsOnly;
            notify.success("Saved!");
            eventEmitter.emit("SHIPMENT_INFO_SAVED", true);

            // On Move, the BE recalculates the start date to the next available week - reflect it in the UI.
            if (overrideClientProductLimits === false && response.startDate) {
                dispatch({ type: "UPDATE_DATES", additionalChanges: { startDate: response.startDate } });
            }

            if (completeCallback) {
                completeCallback();
            } else {
                updateShipmentData(response);
            }
        }

        if (!props.shipmentContext) {
            dispatch({ type: "SAVED" });
        }
    };

    const locationMatch = state.locationValue ? lookups["locations"].find((l) => l.value === state.locationValue) : null;
    const isItemMaster = !!(locationMatch && locationMatch.companyName === "ItemMaster");

    const supplierValue = lookups["suppliers"].find((s) => s.value === state.supplier);
    const cxHClientvalue = lookups["clientsCxH"].find((c) => c.value === state.cxHCompany);
    const receivedBy = lookups.receivers.find((c) => c.value === state.receivedBy);
    const titlePrefix = shipmentData.shipmentType !== 0 ? `${SHIPMENT_TYPE_MAP[shipmentData.shipmentType].label} ` : "";
    const isTaskCompleted = shipmentData.isCompleted;

    return (
        <div>
            <fieldset>
                <legend>{titlePrefix}Shipment Information</legend>
                {state.holdReleasedOnDate && (
                    <NotificationBar
                        type="info"
                        message={`Hold was released on ${format(new Date(state.holdReleasedOnDate), DATE_FNS_SHORT_DATE_FORMAT)}`}
                    />
                )}
                <div className="row">
                    <div className="column -span4">
                        <div className="flex justify-content-between">
                            <DatePicker
                                label="Start Date"
                                name="startDate"
                                value={state.startDate}
                                updateOnChange={updateField}
                            />
                            {isShipmentEditor && (
                                <DatePicker
                                    disabled
                                    label="Received Date"
                                    name="receivedOn"
                                    value={state.receivedOn}
                                    updateOnChange={updateField}
                                />
                            )}
                        </div>
                        <Select
                            required
                            label="Location"
                            name="locationValue"
                            value={state.locationValue}
                            options={lookups["locations"]}
                            updateOnChange={updateField}
                        />
                        {isItemMaster && (
                            <Select
                                label="Shipment Type"
                                name="legacyItemMasterShipmentTypeId"
                                value={state.legacyItemMasterShipmentTypeId}
                                options={lookups["itemMasterShipmentTypes"]}
                                updateOnChange={updateField}
                            />
                        )}
                        {!isDigital && (
                            <Select
                                label="Carrier"
                                name="carrier"
                                value={state.carrier}
                                options={lookups["carriers"]}
                                updateOnChange={updateField}
                            />
                        )}
                        {!isDigital && (
                            <InputList
                                label="Tracking #"
                                name="carrierTrackingNumbers"
                                defaultValue={state.carrierTrackingNumbers}
                                updateOnChange={updateField}
                            />
                        )}
                        <ErrorLabel highlightOnly={!state.isMissingPaperwork && !state.supplier}>
                            <Typeahead
                                label="Supplier"
                                name="supplier"
                                options={lookups["suppliers"]}
                                defaultValue={supplierValue}
                                updateOnChange={updateTypeahead}
                            />
                        </ErrorLabel>
                        <button
                            className="button -text"
                            onClick={() => {
                                setIsAddSupplier(!isAddSupplier);
                            }}
                            type="button"
                            disabled={viewOnlyShipment.disableAddSupplier}
                        >
                            {isAddSupplier ? "- Close New Supplier" : "+ Add New Supplier"}
                        </button>

                        <br />
                        <br />

                        {isAddSupplier && <NewSupplier updateSuppliers={updateSuppliers} />}

                        <ErrorLabel highlightOnly={!state.cxHCompany}>
                            <Typeahead
                                label="CES Client"
                                name="cxHCompany"
                                options={lookups["clientsCxH"]}
                                defaultValue={cxHClientvalue}
                                updateOnChange={updateTypeahead}
                            />
                        </ErrorLabel>
                        <Select
                            required
                            label="Receiver"
                            name="receivedBy"
                            options={lookups["receivers"]}
                            value={state.receivedBy}
                            defaultValue={receivedBy?.value}
                            updateOnChange={updateField}
                        />
                    </div>
                    <div className="column -span4">
                        <Checkbox
                            label="Missing Paperwork"
                            name="isMissingPaperwork"
                            value={state.isMissingPaperwork}
                            updateOnChange={updateField}
                        />
                        <Checkbox
                            label="Missing Payment"
                            name="isMissingPayment"
                            value={state.isMissingPayment}
                            updateOnChange={updateField}
                        />
                        <Checkbox
                            label="Missing Return Data"
                            name="isMissingReturnDate"
                            value={state.isMissingReturnDate}
                            updateOnChange={updateField}
                        />
                        <Checkbox
                            label="Perishable"
                            name="isPerishable"
                            value={state.isPerishable}
                            updateOnChange={updateField}
                        />

                        <VisibleDisabledFeature>
                            <Checkbox
                                label="MDS"
                                name="isMasterDataSolution"
                                value={state.isMasterDataSolution}
                                updateOnChange={updateField}
                            />
                        </VisibleDisabledFeature>
                        <Checkbox
                            label="Foodservice"
                            name="isFoodService"
                            value={state.isFoodService}
                            updateOnChange={updateField}
                        />
                        <Checkbox
                            label="Send to GDSN"
                            name="sendToGdsn"
                            value={state.sendToGdsn}
                            updateOnChange={updateField}
                            disabled={isTaskCompleted}
                        />
                        <Checkbox
                            label="Premium Editing"
                            name="hasPremiumEditingShipmentDefault"
                            value={state.hasPremiumEditingShipmentDefault}
                            updateOnChange={updateField}
                        />
                        <Checkbox
                            label="Client Image Approval"
                            name="imagesNeedClientApprovalDefault"
                            value={state.imagesNeedClientApprovalDefault}
                            updateOnChange={updateField}
                        />

                        <Select
                            label="Final Destination"
                            name="finalDestination"
                            options={lookups["finalDestinations"]}
                            value={state.finalDestination}
                            updateOnChange={updateField}
                        />

                        <div className="group">
                            <h3>Hold</h3>
                            <Checkbox
                                label="On Hold"
                                name="isOnHold"
                                value={state.isOnHold}
                                updateOnChange={updateField}
                            />
                            <DatePicker
                                label="Release Date"
                                name="holdReleaseDate"
                                value={state.holdReleaseDate}
                                updateOnChange={updateField}
                            />
                        </div>
                    </div>
                    <div className="column -span4">
                        <div className="group">
                            <Checkbox
                                label="CMP?"
                                name="isCmp"
                                value={state.isCmp}
                                updateOnChange={updateField}
                            />
                            <div className={state.isCmp ? "" : "disable"}>
                                <Select
                                    label="Payment Options"
                                    name="paymentMethod"
                                    options={lookups["paymentMethods"]}
                                    value={state.paymentMethod}
                                    updateOnChange={updateField}
                                />
                                <Input
                                    label="Payment Reference #"
                                    name="paymentReferenceNumber"
                                    value={state.paymentReferenceNumber}
                                    updateOnChange={updateField}
                                />
                                <Checkbox
                                    label="CMP Confirmed"
                                    name="isCmpConfirmed"
                                    value={state.isCmpConfirmed}
                                    updateOnChange={updateField}
                                />
                            </div>
                        </div>

                        <div className="group">
                            <Checkbox
                                label="Rush?"
                                name="isRush"
                                value={state.isRush}
                                updateOnChange={updateField}
                            />
                            {isBrandVerified ? (
                                <Number
                                    required
                                    step={turnAroundTimeStep}
                                    disabled={isBrandVerified}
                                    inputMode={isBrandVerified ? "decimal" : "numeric"}
                                    label={state.isRush ? "Rush Days" : "Turn Around Time (Days)"}
                                    name="turnAroundDays"
                                    value={state.turnAroundDays}
                                    min="0"
                                    updateOnChange={updateField}
                                />
                            ) : (
                                <div>
                                    <label
                                        htmlFor="turnAroundDays"
                                        className="inline-block m-b-4"
                                    >
                                        {state.isRush ? "Rush Days" : "Turn Around Time (Days)"}
                                    </label>
                                    <Select
                                        required
                                        name="turnAroundDays"
                                        disabled={isBrandVerified}
                                        value={state.turnAroundDays}
                                        onChange={(e) => updateField("turnAroundDays", e.target.value)}
                                        options={(!isDigital && isPeoria) || isVMP ? physicalPeoriaAndVMPTurnaroundOptions : turnaroundOptions}
                                    />
                                </div>
                            )}
                            {state.isRush && (
                                <Input
                                    label="Confirmed By"
                                    name="rushConfirmedBy"
                                    value={state.rushConfirmedBy}
                                    updateOnChange={updateField}
                                />
                            )}
                            {state.turnAroundDays && state.startDate && (
                                <>{`Due: ${format(state.dueDate ? new Date(state.dueDate) : new Date(), DATE_FNS_SHORT_DATE_FORMAT)}`}</>
                            )}
                        </div>

                        {!_.isEmpty(state.sla_counts) && (
                            <div className="group">
                                <SLACounts data={state.sla_counts} />
                            </div>
                        )}
                    </div>
                </div>
                <div className="button-group">
                    <button
                        ref={saveButtonRef}
                        onClick={() => save()}
                        className="button -primary -right"
                    >
                        Save
                    </button>
                </div>
            </fieldset>
        </div>
    );
};

export default ShipmentInformation;
